import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { projectsApi, companiesApi, usersApi } from "../services/api";
import { attachmentCount, ticketHasAttachments } from "../utils/attachments";
import { isSafeBlobType } from "../utils/safeBlob";
import { useNotification } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";

const MAX_ATTACHMENT_SIZE = 20 * 1024 * 1024;
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  "jpg", "jpeg", "png", "gif", "webp", "pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "zip",
]);
const IMAGE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "webp"]);
const CLIPBOARD_IMAGE_EXTENSIONS = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
};
const ATTACHMENT_ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip";

const fileExtension = (name = "") => name.split(".").pop()?.toLowerCase() || "";
const formatFileSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
const pastedImageFilename = (extension, index) => {
  const now = new Date();
  const p = (value) => String(value).padStart(2, "0");
  const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}${p(now.getSeconds())}`;
  return `pasted-image-${stamp}${index ? `-${index + 1}` : ""}.${extension}`;
};

function SelectedAttachment({ file, onRemove }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const isImage = IMAGE_EXTENSIONS.has(fileExtension(file.name));

  useEffect(() => {
    if (!isImage) return undefined;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file, isImage]);

  return (
    <div className="flex min-w-0 items-center gap-2 rounded-lg border bg-white p-2">
      {isImage && previewUrl ? (
        <img src={previewUrl} alt={file.name} className="h-12 w-12 flex-shrink-0 rounded object-cover" />
      ) : (
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded bg-gray-100 text-xl">📄</span>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-gray-700" title={file.name}>{file.name}</p>
        <p className="text-[11px] text-gray-400">{fileExtension(file.name).toUpperCase()} · {formatFileSize(file.size)}</p>
      </div>
      <button type="button" onClick={onRemove} className="flex-shrink-0 text-gray-400 hover:text-red-500" title="הסר קובץ">✕</button>
    </div>
  );
}

function SavedImageAttachment({ attachment, loadImage = projectsApi.viewMilestoneAttachment }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    let active = true;
    let objectUrl;
    loadImage(attachment.id).then((blob) => {
      if (!active) return;
      objectUrl = URL.createObjectURL(blob);
      setUrl(objectUrl);
    }).catch(() => active && setUrl(null));
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, loadImage]);

  if (!url) return <div className="h-24 animate-pulse rounded-lg bg-gray-100" />;
  return (
    <button type="button" onClick={() => window.open(url, "_blank", "noopener,noreferrer")} className="overflow-hidden rounded-lg border bg-gray-50">
      <img src={url} alt={attachment.original_filename} className="max-h-64 w-full object-contain" />
    </button>
  );
}

const PRIORITY_HE  = { low: "נמוכה", medium: "בינונית", high: "גבוהה", critical: "קריטית" };
const STATUS_HE    = { unassigned: "לא הוקצה", open: "פתוח", in_progress: "בטיפול", waiting: "המתנה", closed: "סגור" };   // in_progress: set by the backend on first assignment
const STATUS_BADGE = {
  unassigned: "bg-gray-100 text-gray-600",
  open:       "bg-blue-100 text-blue-700",
  in_progress: "bg-yellow-100 text-yellow-700",
  waiting:    "bg-purple-100 text-purple-700",
  closed:     "bg-gray-100 text-gray-400",
};
const PRIORITY_BADGE = {
  low:      "bg-gray-100 text-gray-600",
  medium:   "bg-yellow-100 text-yellow-700",
  high:     "bg-orange-100 text-orange-700",
  critical: "bg-red-100 text-red-700",
};

const asUtc = (iso) => /Z|[+-]\d\d:\d\d$/.test(iso) ? iso : iso + "Z";
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(asUtc(iso));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(asUtc(iso));
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { addNotification } = useNotification();
  const { user } = useAuth();
  const milestoneUpdateSaving = useRef(false);
  const projectNoteSaving = useRef(false);

  // ── Queries ────────────────────────────────────────────────────────────────
  const { data: project, isLoading, isError: projectFailed, error: projectError, refetch: refetchProject } = useQuery({
    queryKey: ["project", id],
    queryFn: () => projectsApi.get(id),
  });
  const { data: projectNotes = [], isError: notesFailed } = useQuery({
    queryKey: ["project-notes", id],
    queryFn: () => projectsApi.listNotes(id),
  });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => companiesApi.list() });
  const { data: users = [] }     = useQuery({ queryKey: ["users"],     queryFn: () => usersApi.list() });

  // ── Inline edit state ──────────────────────────────────────────────────────
  const [editTitle, setEditTitle]         = useState(false);
  const [titleDraft, setTitleDraft]       = useState("");
  const [editDesc,  setEditDesc]          = useState(false);
  const [descDraft, setDescDraft]         = useState("");

  // ── New milestone form ─────────────────────────────────────────────────────
  const [showMilestoneForm, setShowMilestoneForm] = useState(false);
  const [milestoneForm, setMilestoneForm]         = useState({ title: "", due_date: "" });

  // ── New task forms (per milestone) ─────────────────────────────────────────
  const [taskForms, setTaskForms] = useState({}); // { milestoneId: { open, title, assigned_to } }
  const [updateForms, setUpdateForms] = useState({}); // { milestoneId: { message, files, error } }
  const [projectNoteForm, setProjectNoteForm] = useState({ message: "", files: [], error: "" });
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteMessage, setEditingNoteMessage] = useState("");

  // ── Mutations ──────────────────────────────────────────────────────────────
  // Every mutation below can change a field shown on ProjectsPage/Dashboard's project lists
  // (progress via milestone/task changes, or title/status/assignee via updateProject directly) --
  // invalidate those list caches too, matching how ProjectsPage's own createProject and Dashboard's
  // project mutations already do.
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["project", id] });
    qc.invalidateQueries({ queryKey: ["projects"] });
    qc.invalidateQueries({ queryKey: ["projects-dashboard"] });
    qc.invalidateQueries({ queryKey: ["projects-dashboard-stats"] });
  };

  const updateProject = useMutation({
    mutationFn: (data) => projectsApi.update(id, data),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון הפרויקט", "error"),
  });

  const createMilestone = useMutation({
    mutationFn: (data) => projectsApi.createMilestone(id, data),
    onSuccess: () => { invalidate(); setShowMilestoneForm(false); setMilestoneForm({ title: "", due_date: "" }); },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת אבן הדרך", "error"),
  });

  const updateMilestone = useMutation({
    mutationFn: ({ milestoneId, data }) => projectsApi.updateMilestone(milestoneId, data),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון אבן הדרך", "error"),
  });

  const completeMilestone = useMutation({
    mutationFn: (milestoneId) => projectsApi.completeMilestone(milestoneId),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בסיום אבן הדרך", "error"),
  });

  const deleteMilestone = useMutation({
    mutationFn: (milestoneId) => projectsApi.deleteMilestone(milestoneId),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת אבן הדרך", "error"),
  });

  const createTask = useMutation({
    mutationFn: ({ milestoneId, data }) => projectsApi.createTask(milestoneId, data),
    onSuccess: (_, { milestoneId }) => {
      invalidate();
      setTaskForms((f) => ({ ...f, [milestoneId]: { open: false, title: "", assigned_to: "" } }));
    },
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה ביצירת המשימה", "error"),
  });

  const updateTask = useMutation({
    mutationFn: ({ taskId, data }) => projectsApi.updateTask(taskId, data),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בעדכון המשימה", "error"),
  });

  const completeTask = useMutation({
    mutationFn: (taskId) => projectsApi.completeTask(taskId),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה בסיום המשימה", "error"),
  });

  const deleteTask = useMutation({
    mutationFn: (taskId) => projectsApi.deleteTask(taskId),
    onSuccess: invalidate,
    onError: (err) => addNotification(err?.response?.data?.detail || "שגיאה במחיקת המשימה", "error"),
  });

  const createMilestoneUpdate = useMutation({
    mutationFn: ({ milestoneId, message, files }) => projectsApi.createMilestoneUpdate(milestoneId, message, files),
    onSuccess: (_, { milestoneId }) => {
      setUpdateForms((forms) => ({ ...forms, [milestoneId]: { message: "", files: [], error: "" } }));
      invalidate();
      addNotification("עדכון אבן הדרך נשמר בהצלחה");
    },
    onError: (error, { milestoneId }) => {
      const detail = error?.response?.data?.detail || "שמירת עדכון אבן הדרך נכשלה";
      setUpdateForms((forms) => ({
        ...forms,
        [milestoneId]: { ...(forms[milestoneId] || {}), error: detail },
      }));
      addNotification(detail);
    },
    onSettled: () => { milestoneUpdateSaving.current = false; },
  });

  const createProjectNote = useMutation({
    mutationFn: ({ message, files }) => projectsApi.createNote(id, message, files),
    onSuccess: () => {
      setProjectNoteForm({ message: "", files: [], error: "" });
      qc.invalidateQueries({ queryKey: ["project-notes", id] });
      addNotification("ההערה נשמרה בהצלחה");
    },
    onError: (error) => {
      const detail = error?.response?.data?.detail || "שמירת ההערה נכשלה";
      setProjectNoteForm((form) => ({ ...form, error: detail }));
      addNotification(detail);
    },
    onSettled: () => { projectNoteSaving.current = false; },
  });

  const updateProjectNote = useMutation({
    mutationFn: ({ noteId, message }) => projectsApi.updateNote(id, noteId, message),
    onSuccess: () => {
      setEditingNoteId(null);
      setEditingNoteMessage("");
      qc.invalidateQueries({ queryKey: ["project-notes", id] });
      addNotification("ההערה עודכנה בהצלחה");
    },
    onError: (error) => addNotification(error?.response?.data?.detail || "עדכון ההערה נכשל"),
  });

  const deleteProjectNote = useMutation({
    mutationFn: (noteId) => projectsApi.deleteNote(id, noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-notes", id] });
      addNotification("ההערה נמחקה");
    },
    onError: (error) => addNotification(error?.response?.data?.detail || "מחיקת ההערה נכשלה"),
  });

  // ── Handlers ───────────────────────────────────────────────────────────────
  const saveTitle = () => {
    if (titleDraft.trim() && titleDraft !== project.title)
      updateProject.mutate({ title: titleDraft.trim() });
    setEditTitle(false);
  };

  const saveDesc = () => {
    if (descDraft !== (project.description || ""))
      updateProject.mutate({ description: descDraft });
    setEditDesc(false);
  };

  // Checking → completeTask (auto-cascades milestone); unchecking → updateTask
  const toggleTask = (task) => {
    if (task.status !== "completed") {
      completeTask.mutate(task.id);
    } else {
      updateTask.mutate({ taskId: task.id, data: { status: "open" } });
    }
  };

  const setMilestoneUpdateForm = (milestoneId, patch) => {
    setUpdateForms((forms) => ({
      ...forms,
      [milestoneId]: { message: "", files: [], error: "", ...(forms[milestoneId] || {}), ...patch },
    }));
  };

  const selectMilestoneFiles = (milestoneId, selected) => {
    const files = [...selected];
    const unsupported = files.find((file) => !ALLOWED_ATTACHMENT_EXTENSIONS.has(fileExtension(file.name)));
    if (unsupported) {
      setMilestoneUpdateForm(milestoneId, { error: `סוג הקובץ ${unsupported.name} אינו נתמך` });
      return;
    }
    const oversized = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      setMilestoneUpdateForm(milestoneId, { error: `הקובץ ${oversized.name} גדול מ-20MB` });
      return;
    }
    setUpdateForms((forms) => {
      const current = forms[milestoneId] || { message: "", files: [], error: "" };
      return { ...forms, [milestoneId]: { ...current, files: [...current.files, ...files], error: "" } };
    });
  };

  const saveBlobAs = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  };

  const downloadFile = async (attachment, loader) => {
    try {
      saveBlobAs(await loader(attachment.id), attachment.original_filename);
    } catch (error) {
      addNotification(error?.response?.data?.detail || "הורדת הקובץ נכשלה");
    }
  };

  const downloadMilestoneFile = (attachment) =>
    downloadFile(attachment, projectsApi.downloadMilestoneAttachment);

  const downloadProjectNoteFile = (attachment) =>
    downloadFile(attachment, projectsApi.downloadProjectNoteAttachment);

  const openProjectNoteFile = async (attachment) => {
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    try {
      const blob = await projectsApi.viewProjectNoteAttachment(attachment.id);
      if (!isSafeBlobType(blob?.type)) {
        // safeBlob turns everything but images / PDF into octet-stream: a tab pointed at it stays blank and the browser saves an unnamed
        // blob-UUID file. Save it under its own name instead (the "הורדה" behaviour).
        if (popup) popup.close();
        saveBlobAs(blob, attachment.original_filename);
        return;
      }
      const url = URL.createObjectURL(blob);
      if (popup) popup.location.href = url;
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      if (popup) popup.close();
      addNotification(error?.response?.data?.detail || "פתיחת הקובץ נכשלה");
    }
  };

  const submitMilestoneUpdate = (milestoneId, form) => {
    if (milestoneUpdateSaving.current || (!form.message.trim() && form.files.length === 0)) return;
    milestoneUpdateSaving.current = true;
    createMilestoneUpdate.mutate({ milestoneId, message: form.message, files: form.files });
  };

  const selectProjectNoteFiles = (selected) => {
    const files = [...selected];
    const unsupported = files.find((file) => !ALLOWED_ATTACHMENT_EXTENSIONS.has(fileExtension(file.name)));
    if (unsupported) {
      setProjectNoteForm((form) => ({ ...form, error: `סוג הקובץ ${unsupported.name} אינו נתמך` }));
      return;
    }
    const oversized = files.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      setProjectNoteForm((form) => ({ ...form, error: `הקובץ ${oversized.name} גדול מ-20MB` }));
      return;
    }
    setProjectNoteForm((form) => ({ ...form, files: [...form.files, ...files], error: "" }));
  };

  const pasteProjectNoteImages = (event) => {
    const clipboard = event.clipboardData;
    const fileItems = [...(clipboard?.items || [])].filter((item) => item.kind === "file");
    if (fileItems.length === 0) return;

    const pastedText = clipboard.getData("text/plain");
    event.preventDefault();
    if (pastedText) {
      const textarea = event.currentTarget;
      const selectionStart = textarea.selectionStart;
      const selectionEnd = textarea.selectionEnd;
      setProjectNoteForm((form) => ({
        ...form,
        message: `${form.message.slice(0, selectionStart)}${pastedText}${form.message.slice(selectionEnd)}`,
      }));
      window.requestAnimationFrame(() => {
        const caret = selectionStart + pastedText.length;
        textarea.setSelectionRange(caret, caret);
      });
    }

    const pastedFiles = [];
    for (const item of fileItems) {
      const clipboardFile = item.getAsFile();
      if (!clipboardFile) continue;
      const mimeType = (item.type || clipboardFile.type || "").toLowerCase();
      const extension = CLIPBOARD_IMAGE_EXTENSIONS[mimeType];
      if (!extension) {
        setProjectNoteForm((form) => ({
          ...form,
          error: "התמונה שהודבקה אינה בפורמט נתמך. ניתן להדביק PNG, JPEG, GIF או WEBP.",
        }));
        return;
      }
      if (clipboardFile.size > MAX_ATTACHMENT_SIZE) {
        setProjectNoteForm((form) => ({ ...form, error: "התמונה שהודבקה גדולה מ-20MB." }));
        return;
      }
      const existingName = (clipboardFile.name || "").trim();
      const existingExtension = fileExtension(existingName);
      const extensionMatchesMime = existingExtension === extension
        || (extension === "jpg" && existingExtension === "jpeg");
      const filename = existingName && extensionMatchesMime
        ? existingName
        : pastedImageFilename(extension, pastedFiles.length);
      pastedFiles.push(new File([clipboardFile], filename, {
        type: mimeType,
        lastModified: Date.now(),
      }));
    }

    if (pastedFiles.length > 0) selectProjectNoteFiles(pastedFiles);
  };

  const submitProjectNote = () => {
    if (projectNoteSaving.current || (!projectNoteForm.message.trim() && projectNoteForm.files.length === 0)) return;
    projectNoteSaving.current = true;
    createProjectNote.mutate({ message: projectNoteForm.message, files: projectNoteForm.files });
  };

  const canManageProjectNote = (note) =>
    note.author_id === user?.id || ["admin", "owner"].includes(user?.role);

  if (isLoading) {
    return (
      <div className="flex justify-center py-20" dir="rtl">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }
  if (!project && projectFailed && projectError?.response?.status !== 404) {
    // only a real 404 means "not found": a 500 / network error / expired session is a failed load.
    // `!project`: a FAILED BACKGROUND REFETCH keeps the loaded project (react-query keeps data and sets isError), so
    // a blip on window focus must not replace the page a technician is working in.
    return (
      <div role="alert" className="text-center py-20 text-red-600" dir="rtl">
        <p className="font-medium">לא ניתן לטעון את הפרויקט</p>
        <button onClick={() => refetchProject()} className="mt-2 text-sm text-brand-600 hover:underline">נסה שוב</button>
      </div>
    );
  }
  if (!project) return <p className="text-center py-20 text-gray-400" dir="rtl">פרויקט לא נמצא</p>;

  const sortedMilestones = [...(project.milestones || [])].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="space-y-6 max-w-4xl" dir="rtl">
      {/* Back */}
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
        <span>→</span> חזרה
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border p-6 space-y-5">
        {/* Title */}
        <div>
          {editTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={saveTitle}
              onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setEditTitle(false); }}
              className="text-2xl font-bold w-full border-b-2 border-purple-400 outline-none bg-transparent pb-1"
            />
          ) : (
            <h1
              className="text-2xl font-bold text-gray-900 cursor-pointer hover:text-purple-700 transition-colors"
              onClick={() => { setTitleDraft(project.title); setEditTitle(true); }}
              title="לחץ לעריכה"
            >
              {project.title}
            </h1>
          )}
        </div>

        {/* Source ticket badges (for auto-created projects) */}
        {(project.linked_tasks ?? []).some(t => t.source_ticket) && (
          <div className="flex gap-2 flex-wrap">
            {(project.linked_tasks ?? []).filter(t => t.source_ticket).map(t => (
              <Link
                key={t.id}
                to={`/tickets/${t.source_ticket.id}`}
                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100"
              >
                נוצר מקריאה #{t.source_ticket.ticket_number}
                {ticketHasAttachments(t.source_ticket) && (
                  <span title={`${attachmentCount(t.source_ticket)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-500">
                    📎
                    <span className="text-[10px] font-semibold">{attachmentCount(t.source_ticket)}</span>
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}

        {/* Fields grid */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">סטטוס</label>
            <select
              value={project.status}
              onChange={(e) => updateProject.mutate({ status: e.target.value })}
              className={`border rounded-lg px-2 py-1.5 text-sm w-full font-medium ${STATUS_BADGE[project.status] || ""}`}
            >
              {Object.entries(STATUS_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">עדיפות</label>
            <select
              value={project.priority}
              onChange={(e) => updateProject.mutate({ priority: e.target.value })}
              className={`border rounded-lg px-2 py-1.5 text-sm w-full font-medium ${PRIORITY_BADGE[project.priority] || ""}`}
            >
              {Object.entries(PRIORITY_HE).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">חברה</label>
            <select
              value={project.company_id ?? ""}
              onChange={(e) => updateProject.mutate({ company_id: e.target.value || null })}
              className="border rounded-lg px-2 py-1.5 text-sm w-full text-gray-700"
            >
              <option value="">—</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">אחראי</label>
            <select
              value={project.assigned_to ?? ""}
              onChange={(e) => updateProject.mutate({ assigned_to: e.target.value || null })}
              className="border rounded-lg px-2 py-1.5 text-sm w-full text-gray-700"
            >
              <option value="">—</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-xs text-gray-500 mb-1">תיאור</label>
          {editDesc ? (
            <textarea
              autoFocus
              rows={3}
              value={descDraft}
              onChange={(e) => setDescDraft(e.target.value)}
              onBlur={saveDesc}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
            />
          ) : (
            <p
              onClick={() => { setDescDraft(project.description || ""); setEditDesc(true); }}
              className="text-sm text-gray-600 cursor-pointer hover:bg-gray-50 rounded p-2 min-h-[2.5rem] border border-transparent hover:border-gray-200"
            >
              {project.description || <span className="text-gray-300 italic">לחץ להוספת תיאור…</span>}
            </p>
          )}
        </div>

        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-1.5">
            <span className="font-medium text-gray-700">התקדמות</span>
            <span className="text-gray-500">{project.progress}%</span>
          </div>
          <div className="bg-gray-100 rounded-full h-3">
            <div
              className="bg-purple-500 rounded-full h-3 transition-all duration-500"
              style={{ width: `${project.progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* General project notes */}
      <section className="rounded-xl border bg-white p-4 sm:p-6">
        <h2 className="mb-4 text-base font-semibold text-gray-800">הערות כלליות לפרויקט</h2>

        <div className="mb-5 space-y-2 rounded-lg border bg-gray-50 p-3 sm:p-4">
          <textarea
            rows={3}
            value={projectNoteForm.message}
            onChange={(event) => setProjectNoteForm((form) => ({ ...form, message: event.target.value, error: "" }))}
            onPaste={pasteProjectNoteImages}
            placeholder="כתוב הערה כללית על הפרויקט..."
            className="w-full resize-none rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:border-purple-400"
          />
          {projectNoteForm.files.length > 0 && (
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {projectNoteForm.files.map((file, index) => (
                <SelectedAttachment
                  key={`${file.name}-${file.size}-${index}`}
                  file={file}
                  onRemove={() => setProjectNoteForm((form) => ({
                    ...form,
                    files: form.files.filter((_, fileIndex) => fileIndex !== index),
                    error: "",
                  }))}
                />
              ))}
            </div>
          )}
          {projectNoteForm.error && <p className="text-xs text-red-600">{projectNoteForm.error}</p>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">
              <span>📎</span> צרף תמונות או קבצים
              <input
                type="file"
                multiple
                accept={ATTACHMENT_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  selectProjectNoteFiles(event.target.files || []);
                  event.target.value = "";
                }}
              />
            </label>
            <button
              type="button"
              onClick={submitProjectNote}
              disabled={createProjectNote.isPending || (!projectNoteForm.message.trim() && projectNoteForm.files.length === 0)}
              className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {createProjectNote.isPending ? "שומר ומעלה..." : "שמור הערה"}
            </button>
          </div>
          <p className="text-[11px] text-gray-400">JPG, PNG, GIF, WEBP, PDF, Office, CSV, TXT, ZIP · עד 20MB לקובץ</p>
        </div>

        {notesFailed && projectNotes.length === 0 ? (
          <p role="alert" className="py-3 text-center text-sm text-red-600">לא ניתן לטעון את הערות הפרויקט.</p>
        ) : projectNotes.length === 0 ? (
          <p className="py-3 text-center text-sm text-gray-400">אין עדיין הערות כלליות לפרויקט.</p>
        ) : (
          <div className="space-y-3">
            {projectNotes.map((note) => (
              <article key={note.id} className="rounded-lg border p-3 sm:p-4">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                    <span className="font-medium text-gray-600">{note.author?.display_name || "משתמש"}</span>
                    <span>{fmtDateTime(note.created_at)}</span>
                    {note.is_edited && <span className="rounded bg-gray-100 px-1.5 py-0.5">נערך</span>}
                  </div>
                  {canManageProjectNote(note) && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => { setEditingNoteId(note.id); setEditingNoteMessage(note.message); }}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        ערוך
                      </button>
                      <button
                        type="button"
                        disabled={deleteProjectNote.isPending}
                        onClick={() => {
                          if (window.confirm("למחוק את ההערה ואת כל הקבצים המצורפים אליה?")) deleteProjectNote.mutate(note.id);
                        }}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50"
                      >
                        מחק
                      </button>
                    </div>
                  )}
                </div>

                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <textarea
                      autoFocus
                      rows={3}
                      value={editingNoteMessage}
                      onChange={(event) => setEditingNoteMessage(event.target.value)}
                      className="w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:border-purple-400"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setEditingNoteId(null); setEditingNoteMessage(""); }}
                        className="rounded-lg border px-3 py-1.5 text-xs hover:bg-gray-50"
                      >
                        ביטול
                      </button>
                      <button
                        type="button"
                        disabled={updateProjectNote.isPending || (!editingNoteMessage.trim() && (note.attachments || []).length === 0)}
                        onClick={() => updateProjectNote.mutate({ noteId: note.id, message: editingNoteMessage })}
                        className="rounded-lg bg-purple-600 px-3 py-1.5 text-xs text-white hover:bg-purple-700 disabled:opacity-50"
                      >
                        {updateProjectNote.isPending ? "שומר..." : "שמור שינויים"}
                      </button>
                    </div>
                  </div>
                ) : (
                  note.message && <p className="whitespace-pre-wrap break-words text-sm text-gray-700">{note.message}</p>
                )}

                {(note.attachments || []).some((attachment) => attachment.is_image) && (
                  <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {(note.attachments || []).filter((attachment) => attachment.is_image).map((attachment) => (
                      <SavedImageAttachment
                        key={attachment.id}
                        attachment={attachment}
                        loadImage={projectsApi.viewProjectNoteAttachment}
                      />
                    ))}
                  </div>
                )}
                {(note.attachments || []).some((attachment) => !attachment.is_image) && (
                  <div className="mt-3 space-y-2">
                    {(note.attachments || []).filter((attachment) => !attachment.is_image).map((attachment) => (
                      <div key={attachment.id} className="flex min-w-0 items-center gap-2 rounded-lg border bg-gray-50 p-2">
                        <span className="flex-shrink-0">📎</span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-medium text-gray-700" title={attachment.original_filename}>{attachment.original_filename}</p>
                          <p className="text-[11px] text-gray-400">{fileExtension(attachment.original_filename).toUpperCase()} · {formatFileSize(attachment.size_bytes)}</p>
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2 text-xs font-medium">
                          <button type="button" onClick={() => openProjectNoteFile(attachment)} className="text-purple-600 hover:text-purple-800">פתח</button>
                          <button type="button" onClick={() => downloadProjectNoteFile(attachment)} className="text-purple-600 hover:text-purple-800">הורדה</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Milestones */}
      <div className="space-y-3">
        {sortedMilestones.map((milestone) => {
          const tf = taskForms[milestone.id] || { open: false, title: "", assigned_to: "" };
          const uf = updateForms[milestone.id] || { message: "", files: [], error: "" };
          const sortedTasks = [...(milestone.tasks || [])].sort((a, b) => a.sort_order - b.sort_order);

          return (
            <div key={milestone.id} className="bg-white rounded-xl border overflow-hidden">
              {/* Milestone header */}
              <div className="px-5 py-3.5 border-b bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (milestone.status !== "completed") {
                        completeMilestone.mutate(milestone.id);
                      } else {
                        updateMilestone.mutate({ milestoneId: milestone.id, data: { status: "open" } });
                      }
                    }}
                    className={`w-5 h-5 rounded border-2 flex-shrink-0 transition-colors ${
                      milestone.status === "completed" ? "bg-purple-500 border-purple-500" : "border-gray-300 hover:border-purple-400"
                    }`}
                    title={milestone.status === "completed" ? "בטל סימון" : "סמן אבן דרך + כל המשימות כהושלמו"}
                  >
                    {milestone.status === "completed" && (
                      <svg className="w-3 h-3 text-white m-auto" viewBox="0 0 12 12" fill="currentColor">
                        <path d="M10 3L5 8.5 2 5.5" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                  <span className={`font-semibold text-sm ${milestone.status === "completed" ? "line-through text-gray-400" : "text-gray-800"}`}>
                    {milestone.title}
                  </span>
                  {milestone.due_date && (
                    <span className="text-xs text-gray-400">{fmtDate(milestone.due_date)}</span>
                  )}
                  <span className="text-xs text-gray-400">{sortedTasks.filter(t => t.status === "completed").length}/{sortedTasks.length} משימות</span>
                </div>
                <button
                  onClick={() => { if (window.confirm("למחוק את אבן הדרך?")) deleteMilestone.mutate(milestone.id); }}
                  disabled={deleteMilestone.isPending}
                  className="text-gray-300 hover:text-red-400 transition-colors text-sm disabled:opacity-50"
                  title="מחק אבן דרך"
                >
                  ✕
                </button>
              </div>

              {/* Tasks */}
              <div className="px-5 py-2">
                {sortedTasks.map((task) => (
                  <div key={task.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0 group">
                    <input
                      type="checkbox"
                      checked={task.status === "completed"}
                      onChange={() => toggleTask(task)}
                      className="accent-purple-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                    />
                    <span className={`flex-1 text-sm ${task.status === "completed" ? "line-through text-gray-400" : "text-gray-700"}`}>
                      {task.title}
                    </span>
                    {task.assignee && (
                      <span className="text-xs text-gray-400 flex-shrink-0">{task.assignee.display_name}</span>
                    )}
                    <button
                      onClick={() => { if (window.confirm("למחוק משימה?")) deleteTask.mutate(task.id); }}
                      disabled={deleteTask.isPending}
                      className="text-gray-200 hover:text-red-400 transition-colors text-sm opacity-0 group-hover:opacity-100 disabled:opacity-50"
                    >
                      ✕
                    </button>
                  </div>
                ))}

                {/* Add task form */}
                {tf.open ? (
                  <div className="py-2 space-y-2">
                    <input
                      autoFocus
                      value={tf.title}
                      onChange={(e) => setTaskForms((f) => ({ ...f, [milestone.id]: { ...tf, title: e.target.value } }))}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && tf.title.trim() && !createTask.isPending)
                          createTask.mutate({ milestoneId: milestone.id, data: { title: tf.title.trim(), assigned_to: tf.assigned_to || null } });
                        if (e.key === "Escape")
                          setTaskForms((f) => ({ ...f, [milestone.id]: { open: false, title: "", assigned_to: "" } }));
                      }}
                      placeholder="שם המשימה…"
                      className="w-full border rounded px-2 py-1.5 text-sm"
                    />
                    <div className="flex gap-2">
                      <select
                        value={tf.assigned_to}
                        onChange={(e) => setTaskForms((f) => ({ ...f, [milestone.id]: { ...tf, assigned_to: e.target.value } }))}
                        className="flex-1 border rounded px-2 py-1.5 text-sm text-gray-600"
                      >
                        <option value="">ללא אחראי</option>
                        {users.map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
                      </select>
                      <button
                        onClick={() => {
                          if (tf.title.trim())
                            createTask.mutate({ milestoneId: milestone.id, data: { title: tf.title.trim(), assigned_to: tf.assigned_to || null } });
                        }}
                        disabled={!tf.title.trim() || createTask.isPending}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded text-xs disabled:opacity-50"
                      >
                        הוסף
                      </button>
                      <button
                        onClick={() => setTaskForms((f) => ({ ...f, [milestone.id]: { open: false, title: "", assigned_to: "" } }))}
                        className="border px-3 py-1.5 rounded text-xs hover:bg-gray-50"
                      >
                        ביטול
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setTaskForms((f) => ({ ...f, [milestone.id]: { open: true, title: "", assigned_to: "" } }))}
                    className="py-2 text-xs text-purple-500 hover:text-purple-700 transition-colors"
                  >
                    + הוסף משימה
                  </button>
                )}
              </div>

              {/* Milestone updates */}
              <div className="border-t bg-gray-50/60 px-4 py-4 sm:px-5">
                <p className="mb-3 text-xs font-semibold text-gray-500">עדכוני אבן דרך</p>
                {(milestone.updates || []).length > 0 && (
                  <div className="mb-4 space-y-3">
                    {(milestone.updates || []).map((update) => (
                      <div key={update.id} className="rounded-lg border bg-white p-3">
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-400">
                          <span>{update.author?.display_name || "משתמש"}</span>
                          <span>{fmtDate(update.created_at)}</span>
                        </div>
                        {update.message && <p className="whitespace-pre-wrap break-words text-sm text-gray-700">{update.message}</p>}
                        {(update.attachments || []).some((attachment) => attachment.is_image) && (
                          <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {(update.attachments || []).filter((attachment) => attachment.is_image).map((attachment) => (
                              <SavedImageAttachment key={attachment.id} attachment={attachment} />
                            ))}
                          </div>
                        )}
                        {(update.attachments || []).some((attachment) => !attachment.is_image) && (
                          <div className="mt-3 space-y-2">
                            {(update.attachments || []).filter((attachment) => !attachment.is_image).map((attachment) => (
                              <div key={attachment.id} className="flex min-w-0 items-center gap-2 rounded-lg border bg-gray-50 p-2">
                                <span className="flex-shrink-0">📎</span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-xs font-medium text-gray-700" title={attachment.original_filename}>{attachment.original_filename}</p>
                                  <p className="text-[11px] text-gray-400">{fileExtension(attachment.original_filename).toUpperCase()} · {formatFileSize(attachment.size_bytes)}</p>
                                </div>
                                <button type="button" onClick={() => downloadMilestoneFile(attachment)} className="flex-shrink-0 text-xs font-medium text-purple-600 hover:text-purple-800">
                                  הורדה
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="space-y-2">
                  <textarea
                    rows={2}
                    value={uf.message}
                    onChange={(event) => setMilestoneUpdateForm(milestone.id, { message: event.target.value, error: "" })}
                    placeholder="כתוב עדכון לאבן הדרך…"
                    className="w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:border-purple-400"
                  />
                  {uf.files.length > 0 && (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {uf.files.map((file, index) => (
                        <SelectedAttachment
                          key={`${file.name}-${file.size}-${index}`}
                          file={file}
                          onRemove={() => setMilestoneUpdateForm(milestone.id, {
                            files: uf.files.filter((_, fileIndex) => fileIndex !== index),
                            error: "",
                          })}
                        />
                      ))}
                    </div>
                  )}
                  {uf.error && <p className="text-xs text-red-600">{uf.error}</p>}
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <label className="inline-flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border bg-white px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">
                      <span>📎</span> צרף תמונות או קבצים
                      <input
                        type="file"
                        multiple
                        accept={ATTACHMENT_ACCEPT}
                        className="hidden"
                        onChange={(event) => {
                          selectMilestoneFiles(milestone.id, event.target.files || []);
                          event.target.value = "";
                        }}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => submitMilestoneUpdate(milestone.id, uf)}
                      disabled={createMilestoneUpdate.isPending || (!uf.message.trim() && uf.files.length === 0)}
                      className="rounded-lg bg-purple-600 px-4 py-2 text-xs font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {createMilestoneUpdate.isPending ? "שומר ומעלה…" : "שמור עדכון"}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-400">JPG, PNG, GIF, WEBP, PDF, Office, CSV, TXT, ZIP · עד 20MB לקובץ</p>
                </div>
              </div>
            </div>
          );
        })}

        {/* Linked tickets (tasks from move-queue with no milestone) */}
        {(project.linked_tasks ?? []).length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden">
            <div className="px-5 py-3.5 border-b bg-purple-50 flex items-center gap-2">
              <span className="text-sm font-semibold text-purple-800">קריאות מקושרות</span>
              <span className="text-xs text-purple-500 bg-purple-100 rounded-full px-2 py-0.5">{(project.linked_tasks ?? []).length}</span>
            </div>
            <div className="px-5 py-2">
              {(project.linked_tasks ?? []).map((task) => (
                <div key={task.id} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0 group">
                  <input
                    type="checkbox"
                    checked={task.status === "completed"}
                    onChange={() => toggleTask(task)}
                    className="accent-purple-600 w-4 h-4 flex-shrink-0 cursor-pointer"
                  />
                  <span className={`flex-1 text-sm ${task.status === "completed" ? "line-through text-gray-400" : "text-gray-700"}`}>
                    {task.title}
                  </span>
                  {task.source_ticket && (
                    <Link
                      to={`/tickets/${task.source_ticket.id}`}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 flex-shrink-0"
                    >
                      מקריאה #{task.source_ticket.ticket_number}
                      {ticketHasAttachments(task.source_ticket) && (
                        <span title={`${attachmentCount(task.source_ticket)} קבצים מצורפים`} className="inline-flex items-center gap-0.5 text-slate-500 mr-1">
                          📎
                          <span className="text-[10px] font-semibold">{attachmentCount(task.source_ticket)}</span>
                        </span>
                      )}
                    </Link>
                  )}
                  {task.assignee && (
                    <span className="text-xs text-gray-400 flex-shrink-0">{task.assignee.display_name}</span>
                  )}
                  <button
                    onClick={() => { if (window.confirm("למחוק משימה?")) deleteTask.mutate(task.id); }}
                    disabled={deleteTask.isPending}
                    className="text-gray-200 hover:text-red-400 transition-colors text-sm opacity-0 group-hover:opacity-100 disabled:opacity-50"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add milestone */}
        {showMilestoneForm ? (
          <div className="bg-white rounded-xl border p-4 space-y-3">
            <h4 className="text-sm font-medium text-gray-700">אבן דרך חדשה</h4>
            <div className="flex gap-2">
              <input
                autoFocus
                value={milestoneForm.title}
                onChange={(e) => setMilestoneForm((f) => ({ ...f, title: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && milestoneForm.title.trim() && !createMilestone.isPending)
                    createMilestone.mutate({ title: milestoneForm.title.trim(), due_date: milestoneForm.due_date || null });
                  if (e.key === "Escape") setShowMilestoneForm(false);
                }}
                placeholder="שם אבן הדרך…"
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={milestoneForm.due_date}
                onChange={(e) => setMilestoneForm((f) => ({ ...f, due_date: e.target.value }))}
                className="border rounded-lg px-2 py-2 text-sm"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowMilestoneForm(false)} className="border px-3 py-1.5 rounded-lg text-sm hover:bg-gray-50">ביטול</button>
              <button
                onClick={() => {
                  if (milestoneForm.title.trim())
                    createMilestone.mutate({ title: milestoneForm.title.trim(), due_date: milestoneForm.due_date || null });
                }}
                disabled={!milestoneForm.title.trim() || createMilestone.isPending}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg text-sm disabled:opacity-50"
              >
                {createMilestone.isPending ? "יוצר..." : "הוסף אבן דרך"}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowMilestoneForm(true)}
            className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-purple-300 hover:text-purple-500 transition-colors"
          >
            + הוסף אבן דרך
          </button>
        )}
      </div>
    </div>
  );
}
